import type { FeedEntry } from '~/services/layout/index.ts';
import {
  getClientWidth,
  getToggleButtonsState,
  getWidth,
  getHorizontalListButtonPosition,
  getIsLastCell,
  getInitialScrollOffset,
} from './getters.ts';
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type MutableRefObject,
} from 'react';
import { useReadyState } from '~/hooks/use-ready-state.ts';
import { useResponsiveBreakpoint } from '~/hooks/use-responsive-breakpoint.ts';

const CIRCULAR_SCROLL_RESET_BUFFER_PX = 4;

export function useHorizontalListResize({
  entries,
  ref,
  cellRef,
  nextFeedUrl,
  circularCondition,
  setToggleButtons,
  isLoading,
  gutter,
  componentMaxWidth,
}: {
  entries: FeedEntry[] | undefined;
  ref: any;
  cellRef: any;
  nextFeedUrl: string | undefined;
  circularCondition: boolean;
  setToggleButtons: any;
  isLoading: boolean | undefined;
  gutter: number | undefined;
  componentMaxWidth: number;
}) {
  const readyState = useReadyState();

  const handleResize = (): void => {
    const cellWidth: number = getWidth({
      ref: cellRef,
      gutter,
    });

    if (cellWidth === 0) return;

    const enabled: boolean = isEnabledOnResize({
      entries,
      ref,
      cellRef,
      nextFeedUrl,
      isCircularStarted: circularCondition,
      gutter: 0,
      componentMaxWidth,
    });

    const { left, right } = getToggleButtonsState({
      ref,
      circularCondition,
      nextFeedUrl,
      gutter,
      componentMaxWidth,
    });

    setToggleButtons({
      enabled,
      left,
      right,
      now: new Date().getTime(),
    });
  };

  useEffect(() => {
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    handleResize();
  }, [readyState, isLoading]);
}

function isEnabledOnResize({
  entries,
  ref,
  cellRef,
  nextFeedUrl,
  isCircularStarted,
  gutter,
  componentMaxWidth,
}: {
  entries: FeedEntry[] | undefined;
  ref: any;
  cellRef: any;
  nextFeedUrl: string | undefined;
  isCircularStarted: boolean;
  gutter: number | undefined;
  componentMaxWidth: number;
}): boolean {
  const containerWidth: number = getClientWidth({
    ref,
    gutter,
    componentMaxWidth,
  });

  const cellWidth: number = getWidth({
    ref: cellRef,
    gutter,
  });

  if (!gutter) gutter = 0;

  return (
    !!(
      containerWidth &&
      containerWidth < (cellWidth + gutter) * entries!.length - gutter
    ) ||
    isCircularStarted ||
    !!nextFeedUrl
  );
}

/**
 * Dispach resize event when the right button is enabled
 * @param ref - The horizontal list container ref, this is the most parent container
 * @param isCircularStarted - The circular state
 * @param nextFeedUrl - The next feed url
 * @param gutter - The component horizontal gutter
 * @param componentMaxWidth - The component max width
 */
export function useDispachResizeEvent({
  ref,
  circularCondition,
  nextFeedUrl,
  gutter,
  componentMaxWidth,
}: {
  ref: any;
  circularCondition: boolean;
  nextFeedUrl: string | undefined;
  gutter: number | undefined;
  componentMaxWidth: number;
}) {
  const { right } = getToggleButtonsState({
    ref,
    circularCondition,
    nextFeedUrl,
    gutter,
    componentMaxWidth,
  });

  useEffect(() => {
    if (!right) return;

    setTimeout(() => {
      const event = new Event('resize');
      window.dispatchEvent(event);
    }, 0);
  }, [right]);
}

export type HorizontalListButtonPosition = {
  maxHeight: string;
  isMaxHeightReady: boolean;
};

export function useHorizontalListButtonPosition({
  isNextFeedLoading,
  arrow_position,
  ref,
  cellRef,
  position,
}: {
  isNextFeedLoading: boolean;
  arrow_position: string;
  ref: any;
  cellRef: any;
  position: string;
}): HorizontalListButtonPosition {
  const realTimeButtonPosition = getHorizontalListButtonPosition({
    position: arrow_position,
    ref,
    cellRef,
  });

  const isInvalidRealTimeButtonPosition: boolean =
    realTimeButtonPosition === 'auto' || realTimeButtonPosition === '0px';

  const uninterruptibleButtonPosition: React.MutableRefObject<string> =
    useRef<string>(realTimeButtonPosition);

  useEffect(() => {
    if (isNextFeedLoading || isInvalidRealTimeButtonPosition) return;
    uninterruptibleButtonPosition.current = realTimeButtonPosition;
  }, [isNextFeedLoading, realTimeButtonPosition]);

  if (position !== 'image_center')
    return {
      maxHeight: realTimeButtonPosition,
      isMaxHeightReady: realTimeButtonPosition !== 'auto',
    };

  const maxHeight = isInvalidRealTimeButtonPosition
    ? uninterruptibleButtonPosition.current
    : realTimeButtonPosition;

  const horizontalListButtonPosition = {
    maxHeight,
    isMaxHeightReady: maxHeight !== 'auto' && maxHeight !== '0px',
  };

  return horizontalListButtonPosition;
}

/**
 * Autoscroll feature for the horizontal list.
 * Uses a single-shot timeout (not interval) so the timer can be stopped on user interaction
 * and restarted after scroll completes. Returns start/stop helpers for external controls.
 * @param ref - The horizontal list container ref, this is the most parent container with a ref attached to it.
 * @param cellRef - The cell ref.
 * @param gutter - The component horizontal gutter.
 * @param isLoading - The loading state of client loaded feed.
 * @param scrollAction - The scroll action function that will be executed on autoscroll (respects scrolling strategy).
 * @param feedEntries - The feed entries state.
 * @param autoscrollSwitch - The autoscroll switch.
 * @param autoscrollInterval - The autoscroll interval in seconds.
 * @param loopSwitch - The loop switch of the autoscroll feature.
 * @param componentMaxWidth - The component max width.
 * @returns startAutoscrollTimer / stopAutoscrollTimer helpers to control the scheduler.
 */
export function useHorizontalListAutoScroll({
  ref,
  cellRef,
  gutter,
  isLoading,
  scrollAction,
  feedEntries,
  autoscrollSwitch,
  autoscrollInterval,
  loopSwitch,
  componentMaxWidth,
}: {
  ref: any;
  cellRef: any;
  gutter: any;
  isLoading: boolean | undefined;
  scrollAction: () => void;
  feedEntries: FeedEntry[] | undefined;
  autoscrollSwitch: boolean;
  autoscrollInterval: number;
  loopSwitch: boolean;
  componentMaxWidth: number;
}): { startAutoscrollTimer: () => void; stopAutoscrollTimer: () => void } {
  const autoscrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isUserInteractingRef = useRef<boolean>(false);

  const breakpoint = useResponsiveBreakpoint(true);

  const stopAutoscrollTimer = useCallback(() => {
    if (!autoscrollTimeoutRef.current) return;
    clearTimeout(autoscrollTimeoutRef.current);
    autoscrollTimeoutRef.current = null;
  }, []);

  const scheduleAutoscroll = useCallback(() => {
    if (
      !autoscrollSwitch ||
      isLoading ||
      !feedEntries ||
      feedEntries.length === 0
    ) {
      stopAutoscrollTimer();
      return;
    }

    stopAutoscrollTimer();

    autoscrollTimeoutRef.current = setTimeout(() => {
      if (isUserInteractingRef.current) {
        scheduleAutoscroll();
        return;
      }

      const isLastCell: boolean = getIsLastCell({
        ref,
        cellRef,
        feedEntriesLength: feedEntries.length,
        gutter,
        componentMaxWidth,
      });

      if (!loopSwitch && isLastCell) {
        stopAutoscrollTimer();
        return;
      }

      scrollAction();

      scheduleAutoscroll();
    }, autoscrollInterval * 1000);
  }, [
    autoscrollInterval,
    autoscrollSwitch,
    componentMaxWidth,
    feedEntries,
    gutter,
    isLoading,
    loopSwitch,
    ref,
    cellRef,
    scrollAction,
    stopAutoscrollTimer,
  ]);

  const startAutoscrollTimer = useCallback(() => {
    scheduleAutoscroll();
  }, [scheduleAutoscroll]);

  useStopHorizontalListAutoScroll({
    autoscrollSwitch,
    ref,
    isUserInteractingRef: isUserInteractingRef,
    onUserInteractingChange: useCallback(
      (isInteracting: boolean) => {
        if (isInteracting) {
          stopAutoscrollTimer();
          return;
        }
        startAutoscrollTimer();
      },
      [startAutoscrollTimer, stopAutoscrollTimer]
    ),
  });

  useEffect(() => {
    startAutoscrollTimer();

    return () => {
      stopAutoscrollTimer();
    };
  }, [
    autoscrollSwitch,
    autoscrollInterval,
    breakpoint,
    feedEntries?.length,
    isLoading,
    startAutoscrollTimer,
    stopAutoscrollTimer,
  ]);

  return {
    startAutoscrollTimer,
    stopAutoscrollTimer,
  };
}

/**
 * Resets scroll position seamlessly when crossing duplicate boundary in circular scroll.
 * Monitors scroll position and instantly repositions when user reaches the duplicate segment,
 * creating the illusion of infinite scrolling.
 */
export function useHorizontalListCircularScrollReset({
  ref,
  circularCondition,
  feedEntriesLength,
  originalFeedEntriesLength,
  isLoading,
  isInitialPositioning,
  paddingLeft,
}: {
  ref: MutableRefObject<HTMLElement | null>;
  circularCondition: boolean;
  feedEntriesLength: number;
  originalFeedEntriesLength: number;
  isLoading: boolean | undefined;
  isInitialPositioning?: boolean;
  paddingLeft: number;
}): void {
  const segmentWidthRef = useRef<number>(0);
  const isResettingRef = useRef<boolean>(false);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    const container: HTMLElement | null = ref.current;
    const hasDuplicates =
      circularCondition &&
      originalFeedEntriesLength > 0 &&
      feedEntriesLength === originalFeedEntriesLength * 2;

    if (!container || !hasDuplicates) return;

    const scrollContainer: HTMLElement = container;
    const scrollEventTarget = container as unknown as {
      addEventListener: (
        type: string,
        listener: EventListenerOrEventListenerObject
      ) => void;
      removeEventListener: (
        type: string,
        listener: EventListenerOrEventListenerObject
      ) => void;
    };

    const syncSegmentWidth = () => {
      segmentWidthRef.current = scrollContainer.scrollWidth / 2;
    };

    const performReset = () => {
      if (isLoading || isResettingRef.current || isInitialPositioning) return;

      const segmentWidth = segmentWidthRef.current;
      if (!segmentWidth) return;

      const scrollLeft = scrollContainer.scrollLeft;
      const maxScrollLeft =
        scrollContainer.scrollWidth - scrollContainer.clientWidth;

      // Check if scrolled to the very end (past last duplicate entry)
      const isAtEnd =
        scrollLeft >= maxScrollLeft - CIRCULAR_SCROLL_RESET_BUFFER_PX;

      // Check if scrolled past duplicate segment start
      const isPastDuplicateStart =
        scrollLeft > segmentWidth + CIRCULAR_SCROLL_RESET_BUFFER_PX;

      if (!isAtEnd && !isPastDuplicateStart) return;

      let targetLeft: number;

      if (isAtEnd) {
        // At the very end - jump to last original entry (end of first segment)
        targetLeft = segmentWidth - scrollContainer.clientWidth;
      } else {
        // Past duplicate start - jump back to equivalent position in originals
        targetLeft = scrollLeft - segmentWidth;
      }

      targetLeft = scrollLeft - segmentWidth;
      // Adjust for paddingLeft to align correctly with the start
      if (targetLeft < paddingLeft) targetLeft = 0;

      targetLeft = Math.max(0, targetLeft);
      isResettingRef.current = true;

      scrollContainer.scrollTo({
        left: targetLeft,
        behavior: 'instant',
      });

      rafRef.current = requestAnimationFrame(() => {
        isResettingRef.current = false;
      });
    };

    // Use scrollend event to perform reset after smooth scroll completes
    const handleScrollEnd = () => {
      performReset();
    };

    syncSegmentWidth();

    // Check if browser supports scrollend event
    const supportsScrollEnd = 'onscrollend' in scrollContainer;

    if (supportsScrollEnd) {
      scrollEventTarget.addEventListener('scrollend', handleScrollEnd);
    } else {
      // Fallback: use scroll event with debounce
      let scrollTimeout: NodeJS.Timeout | null = null;
      const handleScrollWithFallback = () => {
        if (scrollTimeout) clearTimeout(scrollTimeout);
        scrollTimeout = setTimeout(() => {
          performReset();
        }, 150);
      };
      scrollEventTarget.addEventListener('scroll', handleScrollWithFallback);

      return () => {
        scrollEventTarget.removeEventListener(
          'scroll',
          handleScrollWithFallback
        );
        if (scrollTimeout) clearTimeout(scrollTimeout);
        if (rafRef.current) cancelAnimationFrame(rafRef.current);
      };
    }

    return () => {
      scrollEventTarget.removeEventListener('scrollend', handleScrollEnd);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [
    circularCondition,
    feedEntriesLength,
    isLoading,
    originalFeedEntriesLength,
    ref,
    isInitialPositioning,
    paddingLeft,
  ]);
}

export function useUpdateIndicatorsIndexOnScroll({
  ref,
  setCurrentIndicatorsIndex,
  indicators_switch,
  circularCondition,
  feedEntry,
  autoscrollSwitch,
  loopSwitch,
  isScrollingByClick,
  isInitialPositioning,
}: {
  ref: any;
  setCurrentIndicatorsIndex: any;
  indicators_switch: boolean;
  circularCondition: boolean;
  feedEntry: FeedEntry[] | undefined;
  autoscrollSwitch: boolean;
  loopSwitch: boolean;
  isScrollingByClick: boolean;
  isInitialPositioning?: boolean;
}) {
  const isScrollingByClickRef = useRef<boolean>(isScrollingByClick);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const postClickUpdateTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const wasScrollingByClickRef = useRef<boolean>(isScrollingByClick);

  isScrollingByClickRef.current = isScrollingByClick;

  const updateIndicatorPosition = useCallback(
    (force?: boolean) => {
      // Skip position-based updates when scrolling is triggered by indicator/arrow buttons
      if (isScrollingByClickRef.current && !force) return;
      if (isInitialPositioning) return;

      if (!feedEntry) return;

      const container = ref?.current;
      if (!container) return;

      const containerRect = container.getBoundingClientRect();
      const containerCenter = containerRect.left + containerRect.width / 2;

      // Find the cell closest to the center of the container
      let centerIndex = 0;
      let minDistance = Infinity;

      const cellsWrapper = Array.from(container.children).filter(
        (el) => !(el instanceof HTMLButtonElement)
      )[0] as HTMLElement;
      if (!cellsWrapper) return;

      const children = cellsWrapper.children;

      for (let i = 0; i < children.length; i++) {
        const child = children[i] as HTMLElement;
        const childRect = child.getBoundingClientRect();

        // Calculate the center of the cell
        const cellCenter = childRect.left + childRect.width / 2;

        // Calculate distance from cell center to container center
        const distance = Math.abs(cellCenter - containerCenter);

        if (distance < minDistance) {
          minDistance = distance;
          centerIndex = i;
        }
      }

      const entriesLength = feedEntry.length - 1;

      const indexToSet =
        circularCondition && centerIndex > entriesLength
          ? centerIndex - entriesLength - 1
          : centerIndex;

      setCurrentIndicatorsIndex(indexToSet);
    },
    [
      ref,
      feedEntry,
      circularCondition,
      setCurrentIndicatorsIndex,
      isInitialPositioning,
    ]
  );

  useEffect(() => {
    if (isInitialPositioning) return;
    if (!feedEntry) return;

    const wasScrollingByClick = wasScrollingByClickRef.current;
    wasScrollingByClickRef.current = isScrollingByClick;

    if (!wasScrollingByClick || isScrollingByClick) return;

    if (postClickUpdateTimeoutRef.current) {
      clearTimeout(postClickUpdateTimeoutRef.current);
    }

    postClickUpdateTimeoutRef.current = setTimeout(() => {
      updateIndicatorPosition(true);
      postClickUpdateTimeoutRef.current = null;
    }, 0);

    return () => {
      if (postClickUpdateTimeoutRef.current) {
        clearTimeout(postClickUpdateTimeoutRef.current);
        postClickUpdateTimeoutRef.current = null;
      }
    };
  }, [
    indicators_switch,
    isInitialPositioning,
    isScrollingByClick,
    feedEntry,
    updateIndicatorPosition,
  ]);

  useEffect(() => {
    if (!indicators_switch) return;

    const container = ref.current;
    if (!container) return;

    const scrollEventTarget = container as unknown as {
      addEventListener: (
        type: string,
        listener: EventListenerOrEventListenerObject
      ) => void;
      removeEventListener: (
        type: string,
        listener: EventListenerOrEventListenerObject
      ) => void;
    };

    // Check if browser supports scrollend event
    const supportsScrollEnd = 'onscrollend' in container;

    if (supportsScrollEnd) {
      // Use scrollend event for accurate detection after smooth scroll completes
      const handleScrollEnd = () => {
        updateIndicatorPosition(true);
      };

      scrollEventTarget.addEventListener('scrollend', handleScrollEnd);

      return () => {
        scrollEventTarget.removeEventListener('scrollend', handleScrollEnd);
      };
    } else {
      // Fallback: use scroll event with debounce for browsers without scrollend support
      const handleScrollWithFallback = () => {
        if (scrollTimeoutRef.current) {
          clearTimeout(scrollTimeoutRef.current);
        }
        scrollTimeoutRef.current = setTimeout(() => {
          updateIndicatorPosition();
        }, 150);
      };

      scrollEventTarget.addEventListener('scroll', handleScrollWithFallback);

      return () => {
        scrollEventTarget.removeEventListener(
          'scroll',
          handleScrollWithFallback
        );
        if (scrollTimeoutRef.current) {
          clearTimeout(scrollTimeoutRef.current);
        }
      };
    }
  }, [
    indicators_switch,
    autoscrollSwitch,
    loopSwitch,
    updateIndicatorPosition,
  ]);
}

export function useHorizontalListScroll({
  ref,
  circularCondition,
  setToggleButtons,
  nextFeedUrl,
  isScrolling,
  setIsScrolling,
  setIsScrollingByClick,
  gutter,
  componentMaxWidth,
  isInitialPositioning,
}: {
  ref: any;
  circularCondition: boolean;
  setToggleButtons: any;
  nextFeedUrl: string | undefined;
  isScrolling: boolean;
  setIsScrolling: Function;
  setIsScrollingByClick: Function;
  gutter: number | undefined;
  componentMaxWidth: number;
  isInitialPositioning?: boolean;
}): void {
  const isScrollingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isScrollingByClickTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleScroll = () => {
    if (isInitialPositioning) return;

    const { right, left } = getToggleButtonsState({
      ref,
      circularCondition,
      nextFeedUrl,
      gutter,
      componentMaxWidth,
    });

    setToggleButtons((prev: any) =>
      prev.left !== left || prev.right !== right
        ? {
            ...prev,
            left,
            right,
          }
        : prev
    );

    if (!isScrolling) setIsScrolling(true);

    if (isScrollingTimeoutRef.current) {
      clearTimeout(isScrollingTimeoutRef.current);
    }

    isScrollingTimeoutRef.current = setTimeout(() => {
      setIsScrolling(false);
    }, 200);

    if (isScrollingByClickTimeoutRef.current) {
      clearTimeout(isScrollingByClickTimeoutRef.current);
    }

    isScrollingByClickTimeoutRef.current = setTimeout(() => {
      setIsScrollingByClick(false);
    }, 100);
  };

  useEffect(() => {
    handleScroll();

    ref.current.addEventListener('scroll', handleScroll);

    return () => {
      if (!ref.current) return;
      ref.current?.removeEventListener('scroll', handleScroll);
      if (isScrollingTimeoutRef.current) {
        clearTimeout(isScrollingTimeoutRef.current);
      }
      if (isScrollingByClickTimeoutRef.current) {
        clearTimeout(isScrollingByClickTimeoutRef.current);
      }
    };
  }, []);
}

/**
 * Stop the autoscroll when the user interacts with the horizontal list.
 * Listens to scroll start to flag interaction and uses scrollend when available;
 * otherwise a fallback timer clears the flag after no scroll events for ~200ms.
 * Calls onUserInteractingChange so callers can pause/resume their timers.
 * @param autoscrollSwitch - The autoscroll switch.
 * @param ref - The horizontal list container ref, this is the most parent container.
 * @param isUserInteractingRef - Shared ref that mirrors the interaction state.
 * @param onUserInteractingChange - Optional callback fired when interaction state changes.
 */
function useStopHorizontalListAutoScroll({
  autoscrollSwitch,
  isUserInteractingRef,
  ref,
  onUserInteractingChange,
}: {
  autoscrollSwitch: boolean;
  isUserInteractingRef: any;
  ref: any;
  onUserInteractingChange?: (isUserInteracting: boolean) => void;
}) {
  const SCROLL_END_FALLBACK_DELAY_MS = 200;
  const [isUserInteracting, setIsUserInteracting] = useState<boolean>(false);

  isUserInteractingRef.current = isUserInteracting;

  useEffect(() => {
    if (!autoscrollSwitch) return;

    const element = ref?.current;
    if (!element) return;

    let scrollEndTimeout: ReturnType<typeof setTimeout> | null = null;
    const supportsScrollEnd: boolean = 'onscrollend' in element;

    const clearScrollEndTimeout = () => {
      if (!scrollEndTimeout) return;
      clearTimeout(scrollEndTimeout);
      scrollEndTimeout = null;
    };

    const handleScrollEndFallback = () => {
      if (!isUserInteractingRef.current) return;
      setIsUserInteracting(false);
      onUserInteractingChange && onUserInteractingChange(false);

      clearScrollEndTimeout();
    };

    const handleScrollStart = () => {
      if (!isUserInteractingRef.current) return;
      setIsUserInteracting(true);
      onUserInteractingChange && onUserInteractingChange(true);

      clearScrollEndTimeout();

      scrollEndTimeout = setTimeout(
        handleScrollEndFallback,
        SCROLL_END_FALLBACK_DELAY_MS
      );
    };

    const handleScrollEnd = () => {
      if (!isUserInteractingRef.current) return;
      setIsUserInteracting(false);
      onUserInteractingChange && onUserInteractingChange(false);

      clearScrollEndTimeout();
    };

    // Stop autoscroll when user starts scrolling
    element.addEventListener('scroll', handleScrollStart);

    // Restart autoscroll when scrolling ends
    if (supportsScrollEnd) {
      element.addEventListener('scrollend', handleScrollEnd);
    }

    return () => {
      element.removeEventListener('scroll', handleScrollStart);
      if (supportsScrollEnd) {
        element.removeEventListener('scrollend', handleScrollEnd);
      }
      clearScrollEndTimeout();
    };
  }, [autoscrollSwitch, isUserInteractingRef, onUserInteractingChange, ref]);
}

export function useHorizontalListSnap({
  ref,
  isScrolling,
  isOneCellPerView,
}: {
  ref: any;
  isScrolling: boolean;
  isOneCellPerView: boolean;
}): void {
  useEffect(() => {
    if (!ref.current) return;

    if (isScrolling || isOneCellPerView) {
      ref.current.style.scrollSnapType = 'none';
      return;
    }

    ref.current.style.scrollSnapType = 'x mandatory';
  }, [isScrolling, isOneCellPerView]);
}

/**
 * Positions the horizontal list at the first duplicated entry on initial render,
 * showing the previous item via nextCellOffset peek. Only applies when circular
 * scrolling is enabled. Sets scroll position synchronously without triggering
 * scroll events or updating indicators.
 * @param scrollRef - Ref to the scrollable container.
 * @param cellRef - Ref to the first cell for width calculation.
 * @param circularCondition - Whether circular scrolling is enabled.
 * @param originalEntriesLength - Number of original entries (before duplication).
 * @param gutter - Gutter between cells.
 * @param nextCellOffset - Offset to show peek of previous cell.
 * @param paddingLeft - Left padding of the scroll container.
 * @param isClient - Whether rendering on client-side.
 * @param enabled - Whether initial scroll positioning is enabled.
 * @returns Ref indicating if initial positioning is in progress.
 */
export function useInitialScrollToFirstDuplicate({
  scrollRef,
  cellRef,
  circularCondition,
  originalEntriesLength,
  gutter,
  nextCellOffset,
  enabled,
}: {
  scrollRef: MutableRefObject<HTMLElement | null>;
  cellRef: MutableRefObject<HTMLElement | null>;
  circularCondition: boolean;
  originalEntriesLength: number;
  gutter: number;
  nextCellOffset: number;
  enabled: boolean;
}): boolean {
  const [isInitialPositioning, setIsInitialPositioning] = useState<boolean>(
    enabled && circularCondition
  );

  useEffect(() => {
    if (!enabled || !circularCondition) {
      setIsInitialPositioning(false);
      return;
    }
    if (!scrollRef.current || !cellRef.current) {
      setIsInitialPositioning(false);
      return;
    }
    if (originalEntriesLength <= 0) {
      setIsInitialPositioning(false);
      return;
    }

    // Get pure cell width without gutter for initial scroll calculation
    const cellWidth = getWidth({ ref: cellRef, gutter }) - (gutter || 0);

    if (!cellWidth || cellWidth <= 0) {
      setIsInitialPositioning(false);
      return;
    }

    const offset = getInitialScrollOffset({
      cellWidth,
      nextCellOffset,
      originalEntriesCount: originalEntriesLength,
      marginRight: gutter || 0,
    });

    if (offset <= 0) {
      setIsInitialPositioning(false);
      return;
    }
    setTimeout(() => {
      scrollRef?.current?.scrollTo({
        left: offset,
        behavior: 'instant',
      });
    }, 100);

    setTimeout(() => {
      setIsInitialPositioning(false);
    }, 500);
  }, [enabled]);

  return isInitialPositioning;
}
